<template>
  <v-app-bar app color="white" elevation="1">
    <v-app-bar-nav-icon @click="ui.toggleDrawer()" />

    <v-toolbar-items class="ml-2">
      <v-breadcrumbs dense>
        <template v-for="(crumb, idx) in breadcrumbs" :key="idx">
          <v-breadcrumbs-item :to="crumb.to" :disabled="idx === breadcrumbs.length - 1">
            {{ crumb.text }}
          </v-breadcrumbs-item>

          <!-- Insert a slash if this is not the last item -->
          <span v-if="idx < breadcrumbs.length - 1" class="mx-1" style="user-select: none">/</span>
        </template>
      </v-breadcrumbs>
    </v-toolbar-items>

    <v-toolbar-title class="ml-4"></v-toolbar-title>
    <v-spacer />

    <!-- ─── TODAY’S DATE ──────────────────────────────────────────────────── -->
    <!-- Always visible; small font so it fits on mobile. -->
    <span class="mr-4 text-caption">{{ todayString }}</span>
  </v-app-bar>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRoute } from "vue-router";
import { useUIStore } from "@/stores/ui";
import { useAuthStore } from "@/stores/auth";
import { userManager } from "@/auth/oidc";
import { useTheme } from "vuetify";

const ui = useUIStore();
const auth = useAuthStore();
const route = useRoute();

const breadcrumbs = computed(() => {
  return route.matched
    .filter((r) => r.meta?.breadcrumb)
    .map((r) => {
      // Special links for parent routes
      if (r.name === "ZoneParent") {
        return { text: r.meta.breadcrumb, to: { name: "ZoneList" } };
      }
      if (r.name === "FormParent") {
        return { text: r.meta.breadcrumb, to: { name: "FormList" } };
      }

      // Identify which params are valid for this route
      const validParams = Object.keys(route.params).filter((key) => r.path.includes(`:${key}`));

      const params = validParams.length
        ? Object.fromEntries(validParams.map((k) => [k, route.params[k]]))
        : undefined;

      return {
        text: r.meta.breadcrumb,
        to: r.name ? { name: r.name, params } : { path: r.path },
      };
    });
});

// ─── TODAY’S DATE FORMATTED ───────────────────────────────────────────────
const todayString = computed(() => {
  const d = new Date();
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
});

// ─── USER PROFILE ACTIONS ──────────────────────────────────────────────
async function onLogout() {
  if (confirm("Are you sure you want to logout?")) {
    try {
      // 1. Call your auth store logout (handles token cleanup, etc.)
      await auth.logout();

      // 2. The auth store should handle the redirect
      // If not, add: router.push('/login')
    } catch (error) {
      console.error("Logout error:", error);

      // Fallback: Force logout
      auth.clearUser();
      localStorage.clear(); // Clear any remaining tokens
      window.location.href = "/login";
    }
  }
}
</script>

<style scoped>
.v-breadcrumbs {
  font-size: 0.875rem; /* ~14px so it fits nicely in the app-bar */
  margin-top: 0;
  margin-bottom: 0;
}

.text-caption {
  font-size: 0.85rem !important;
}
</style>
